// Component dependencies are ordered correctly to ensure all components are defined before usage.

import { Box, Stack, Title } from '@mantine/core';
import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Switch } from '../../../components/reactHookFormMantine/Switch.tsx';
import { useI18n } from '../../../hooks/useI18n.ts';
import type { IncompleteEventFormComplete } from '../IncompleteEventForm/schema.ts';
import { DateTimeControls } from './components/DateTimeControls.tsx';
import { EventTypeSegmentedControl } from './components/EventTypeSegmentedControl.tsx';
import { RecurrenceSettings } from './components/RecurrenceSettings.tsx';

export const WhereAndWhen: FC<{
  useFormValue: UseFormReturn<IncompleteEventFormComplete>;
}> = ({ useFormValue }) => {
  const { control, watch, setValue, clearErrors } = useFormValue;

  const { t } = useI18n();

  const activeEventType = watch('eventType');
  const previousEventType = useRef(activeEventType);

  useEffect(() => {
    if (previousEventType.current === activeEventType) return;
    const prev = previousEventType.current;
    previousEventType.current = activeEventType;

    if (prev === 'online') {
      setValue('onlineLink', '');
      clearErrors('onlineLink');
    }
    if (prev === 'live') {
      setValue('venue', null);
      setValue('customVenue', '');
      clearErrors('venue');
    }
  }, [activeEventType, setValue, clearErrors]);
  const recurrenceActive = watch('recurrenceActive');
  const start = watch('start');
  const end = watch('end');

  return (
    <Stack gap="xxs" justify="flex-start">
      <Title order={4}>{t('where-and-when_title--where') ?? 'Where'}</Title>
      <EventTypeSegmentedControl activeEventType={activeEventType} control={control} />

      <Title mt="sm" order={4}>
        {t('where-and-when_title--when') ?? 'When'}
      </Title>
      <DateTimeControls
        control={control}
        end={end}
        recurrenceActive={recurrenceActive}
        setValue={setValue}
        start={start}
      />

      <Box bd="1px solid gray.3" mt="sm" p="sm" style={{ borderRadius: 12 }}>
        <Switch
          control={control}
          label={t('where-and-when_label--repeating-event') ?? 'Repeating event'}
          name="recurrenceActive"
        />

        {recurrenceActive && (
          <Box mt="sm">
            <RecurrenceSettings useFormValue={useFormValue} />
          </Box>
        )}
      </Box>
    </Stack>
  );
};
